// Bê từ finiva-admin src/modules/auth/components/ChangePasswordModal.tsx. Sửa: không
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/utils'
import { useChangePassword } from '../hooks/useAuth'

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Vui lòng nhập mật khẩu hiện tại'),
    newPassword: z.string().min(6, 'Mật khẩu mới ít nhất 6 ký tự'),
    confirmPassword: z.string().min(1, 'Vui lòng xác nhận mật khẩu'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
}

function PasswordField({
  id,
  label,
  error,
  registration,
}: {
  id: string
  label: string
  error?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registration: any
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete="off"
          className={cn(
            'h-8 w-full rounded-lg border border-border bg-background px-2 pr-8 text-sm text-foreground',
            'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
            error && 'border-destructive focus:border-destructive focus:ring-destructive/20',
          )}
          {...registration}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-muted-foreground"
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-state-critical">{error}</p>}
    </div>
  )
}

export function ChangePasswordModal({ open, onClose }: Props) {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useChangePassword()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  if (!open) return null

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (values: FormValues) => {
    try {
      await mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      handleClose()
    } catch {
      // error handled by mutation onError (toast)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
        <h2 className="mb-5 text-base font-semibold text-foreground">
          {t('auth.changePasswordTitle')}
        </h2>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <PasswordField
            id="currentPassword"
            label={t('auth.currentPassword')}
            error={errors.currentPassword?.message}
            registration={register('currentPassword')}
          />
          <PasswordField
            id="newPassword"
            label={t('auth.newPassword')}
            error={errors.newPassword?.message}
            registration={register('newPassword')}
          />
          <PasswordField
            id="confirmPassword"
            label={t('auth.confirmNewPassword')}
            error={errors.confirmPassword?.message}
            registration={register('confirmPassword')}
          />

          <div className="mt-1 flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="flex-1" isLoading={isPending}>
              {isPending ? t('auth.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
